import { useId, useMemo } from 'react';

import { RadioGroup } from '@/components/ui/form/RadioGroup';
import {
  RecurringEditScope,
  type RecurringEditScope as RecurringEditScopeValue
} from '@/types/enums';

import { recurrenceScopeLabel } from './recurrenceScopeLabel';

const recurrenceScopeValues = [
  RecurringEditScope.This,
  RecurringEditScope.ThisAndFuture,
  RecurringEditScope.All
];

export interface RecurrenceScopeInputProps {
  value?: RecurringEditScopeValue | null;
  onChange: (value: RecurringEditScopeValue) => void;
  /** Scopes the person cannot choose, each with the reason shown under its option. */
  disabledScopes?: Partial<Record<RecurringEditScopeValue, string>>;
  name?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
}

export function RecurrenceScopeInput({
  value,
  onChange,
  disabledScopes,
  name,
  disabled = false,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy
}: RecurrenceScopeInputProps) {
  const generatedName = useId();
  const options = useMemo(
    () =>
      recurrenceScopeValues.map((value) => {
        const reason = disabledScopes?.[value];

        return {
          value,
          label: recurrenceScopeLabel(value),
          disabled: reason !== undefined,
          description: reason
        };
      }),
    [disabledScopes]
  );

  return (
    <RadioGroup
      name={name ?? `recurrence-scope-${generatedName}`}
      options={options}
      value={value ?? ''}
      onChange={(nextValue) => onChange(nextValue as RecurringEditScopeValue)}
      orientation="vertical"
      variant="card"
      className={className}
      disabled={disabled}
      id={id}
      aria-describedby={ariaDescribedBy}
      aria-labelledby={ariaLabelledBy}
    />
  );
}
